import { ActionIcon, Button } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconPencil } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { EventDetails } from 'src/Components/Calendars/EventDetails';
import styles from 'src/Components/Calendars/EventDetails/EventDetails.module.scss';
import { AnimatedRoutedDrawer } from 'src/Components/Drawers/AnimatedRoutedDrawer';
import { LinkedEntitiesSection } from 'src/Components/EntityLinks/LinkedEntitiesSection';
import { RelatedTasksList } from 'src/Components/Tasks/RelatedTasksList';
import { useDeleteEvent } from 'src/Hooks/Mutations/useDeleteEvent';
import useFetchEvent from 'src/Hooks/Queries/useFetchEvent';
import { useIsAssetAboveQuotas } from 'src/Hooks/Shared/useIsAssetAboveQuotas';
import { LinkableEntityType } from 'src/Services/API/EntityLinks.api';

import './ViewEventDrawer.module.scss';

type EventParams = {
  eventId: string;
};

const ViewEventDrawer = () => {
  const { t } = useTranslation();
  const { assetId, eventId } = useParams<{ assetId: string } & EventParams>();
  const [searchParams] = useSearchParams();
  const occurrenceStartTime = useMemo(() => {
    const startTime = searchParams.get('startTime');
    if (startTime) return dayjs(startTime);
  }, [searchParams]);
  const navigate = useNavigate();
  const { mutate: deleteEvent } = useDeleteEvent();
  const assetAboveQuotas = useIsAssetAboveQuotas();

  const { data: event, isLoading } = useFetchEvent(eventId);

  if (!eventId) {
    return <Navigate to="/planning" replace />;
  }

  return (
    <AnimatedRoutedDrawer
      title={t('eventDetailsDrawer.title', 'Événement')}
      to={`/asset/${assetId}/planning`}
    >
      {({ onClose }) => {
        const openModal = () =>
          modals.openConfirmModal({
            title: t('event.delete.youreDeletingAnEvent', 'Vous supprimez un événement'),
            children: (
              <div>
                {t(
                  'event.delete.allOrOccurrence',
                  "Voulez-vous supprimer toutes les occurrences de cet événement ou seulement l'occurrence sélectionnée ?"
                )}
              </div>
            ),
            labels: {
              confirm: t('event.delete.deleteAllEvents', 'Supprimer toutes les occurrences'),
              cancel: t(
                'event.delete.deleteSingleOccurrence',
                'Supprimer seulement cette occurrence'
              ),
            },
            onCancel: () => {
              deleteEvent({ eventId, occurrenceDate: occurrenceStartTime });
              onClose();
            },
            onConfirm: () => {
              deleteEvent({ eventId });
              onClose();
            },
            confirmProps: {
              variant: 'danger',
            },
            cancelProps: {
              variant: 'danger',
            },
          });

        return (
          <EventDetails
            event={event}
            isLoading={isLoading}
            occurrenceStartTime={occurrenceStartTime}
            rightTitleSection={
              <ActionIcon
                variant={'transparent'}
                onClick={() =>
                  navigate('edit', { state: { disableAnimation: true }, replace: true })
                }
                className={styles.icon}
                disabled={assetAboveQuotas}
              >
                <IconPencil size={21} />
              </ActionIcon>
            }
            bottomSection={
              <>
                {event?.id && (
                  <LinkedEntitiesSection
                    entityType={LinkableEntityType.Event}
                    entityId={event.id}
                    entityTitle={event.name}
                  />
                )}
                {event?.id && <RelatedTasksList entityType="event" entityId={event.id} />}
                <div className={styles.buttons}>
                  <Button
                    onClick={() => {
                      if (event?.id) {
                        if (event.isRecurring) {
                          openModal();
                        } else {
                          deleteEvent({ eventId: event.id, occurrenceDate: occurrenceStartTime });
                          onClose();
                        }
                      }
                    }}
                    variant="danger"
                  >
                    {t('common.delete', 'Supprimer')}
                  </Button>
                </div>
              </>
            }
          />
        );
      }}
    </AnimatedRoutedDrawer>
  );
};

export default ViewEventDrawer;
